import styled, { css } from 'styled-components';
import {
  DisplayLgReg,
  DisplayMedReg,
  DisplaySmMobileReg,
  TitleLgReg,
  TitleSmReg,
  BodyLgReg,
} from '@seed-health/tokens';
import { mqMediumMin, mqLargeMin } from 'styled/tools/media-queries';
import type { ProductLockupSize, ProductLockupOrientation } from '.';

// Product-name typography per size. The pill (SKU badge) sizing lives in the
// composed Pill component; these only style the name set beside it.
const nameStyles: Record<ProductLockupSize, ReturnType<typeof css>> = {
  xl: css`
    ${DisplayLgReg}
  `,
  lg: css`
    ${DisplayMedReg}
  `,
  md: css`
    ${DisplaySmMobileReg}
  `,
  sm: css`
    ${TitleLgReg}
  `,
  xs: css`
    ${TitleSmReg}
  `,
  xxs: css`
    ${BodyLgReg}
  `,
};

// Gap between the pill and the product name per size, per orientation.
const gapStyles: Record<
  ProductLockupOrientation,
  Record<ProductLockupSize, string>
> = {
  landscape: {
    xl: 'var(--spacing-16)',
    lg: '12px',
    md: '10px',
    sm: 'var(--spacing-8)',
    xs: '6px',
    xxs: '6px',
  },
  portrait: {
    xl: '10px',
    lg: '10px',
    md: '6px',
    sm: '5px',
    xs: '2px',
    xxs: '2px',
  },
};

export const LockupWrapper = styled.span<{
  $orientation: ProductLockupOrientation;
  $light?: boolean;
  $centered?: boolean;
  $sizeBase: ProductLockupSize;
  $sizeTablet?: ProductLockupSize;
  $sizeDesktop?: ProductLockupSize;
}>`
  display: inline-flex;
  flex-direction: ${({ $orientation }) =>
    $orientation === 'portrait' ? 'column' : 'row'};
  align-items: ${({ $orientation, $centered }) =>
    $orientation === 'portrait' && !$centered ? 'flex-start' : 'center'};
  justify-content: center;
  width: fit-content;
  max-width: 100%;
  color: ${({ $light }) =>
    $light ? 'var(--text-inverse-primary)' : 'var(--text-primary)'};

  gap: ${({ $orientation, $sizeBase }) => gapStyles[$orientation][$sizeBase]};

  ${({ $orientation, $sizeTablet }) =>
    $sizeTablet &&
    css`
      @media ${mqMediumMin} {
        gap: ${gapStyles[$orientation][$sizeTablet]};
      }
    `}

  ${({ $orientation, $sizeDesktop }) =>
    $sizeDesktop &&
    css`
      @media ${mqLargeMin} {
        gap: ${gapStyles[$orientation][$sizeDesktop]};
      }
    `}
`;

export const ProductName = styled.span<{
  $sizeBase: ProductLockupSize;
  $sizeTablet?: ProductLockupSize;
  $sizeDesktop?: ProductLockupSize;
}>`
  color: inherit;

  ${({ $sizeBase }) => nameStyles[$sizeBase]}

  ${({ $sizeTablet }) =>
    $sizeTablet &&
    css`
      @media ${mqMediumMin} {
        ${nameStyles[$sizeTablet]}
      }
    `}

  ${({ $sizeDesktop }) =>
    $sizeDesktop &&
    css`
      @media ${mqLargeMin} {
        ${nameStyles[$sizeDesktop]}
      }
    `}
`;
